import { Calendar, Plus } from 'lucide-react';

import type { TeamMember } from '~/ui-rocco/pages/detail-shared';
import { button } from '~/ui-rocco/button-system';

/**
 * One team-member slide — the TeamGrid tile (portrait, name, role), with a
 * Book a meeting control resting on the portrait.
 *
 * The member is offered by name because the sections that draw this only
 * render people the professional has made public, so the reader is choosing
 * a face rather than being handed a roster to pick from afterwards.
 *
 * Drawn by the overview's Who we are carousel and, on the phone, the About
 * tab's Meet the Team grid.
 */
export const TeamMemberSlide = ({
    member,
    onBookMeeting,
    onOpen,
}: {
    member: TeamMember;
    onBookMeeting?: (member: TeamMember) => void;
    onOpen: (member: TeamMember) => void;
}) => (
    <div className="flex flex-col">
        <div className="bg-gray-200 relative aspect-[437/524] w-full overflow-hidden rounded-tile">
            {member.image ? (
                <img
                    src={member.image}
                    alt={member.name}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
            {onBookMeeting ? (
                <button
                    type="button"
                    onClick={() => onBookMeeting(member)}
                    // Named here because the label is hidden on a phone.
                    aria-label="Book a meeting"
                    // The platform button rather than its own pill: this rests
                    // on a photograph beside Save and Visit site, which are the
                    // lifted family, and it was reading as a smaller control
                    // from an older page. Position only through `extra` — the
                    // height, corner and padding belong to the size step.
                    //
                    // The tile-control step: a square on the phone, where the
                    // portrait is 176px wide and the labelled L ran 199px, out
                    // past the picture it sits on. It grows back to the L with
                    // its label from `md`, where there is room for one.
                    className={button(
                        'lifted',
                        ['m', 'l'],
                        'absolute bottom-4 left-4',
                        { icon: 'max-md' },
                    )}
                >
                    {/* Plain calendar, not CalendarPlus: this tile leads to a
                        booking for a member with a connected calendar and to a
                        preferred-time request for everyone else, so the icon
                        cannot promise something lands in a diary. */}
                    <Calendar
                        aria-hidden="true"
                        className="size-[1.125rem] shrink-0"
                    />
                    <span className="max-md:hidden">Book a meeting</span>
                </button>
            ) : null}
            {/* The project page's pair: the booking on the left, the person on
                the right, both square at the phone step. */}
            <button
                type="button"
                onClick={() => onOpen(member)}
                aria-label={`About ${member.name}`}
                className={button(
                    'lifted',
                    ['m', 'l'],
                    'absolute right-4 bottom-4',
                    { icon: true },
                )}
            >
                <Plus aria-hidden="true" className="size-[1.125rem] shrink-0" />
            </button>
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-[#181818] max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[0.875rem]">
            {member.name}
        </p>
        {member.jobTitle ? (
            <p className="m-0 font-sans leading-snug text-[#181818]/40 max-md:text-sm md:text-[0.875rem]">
                {member.jobTitle}
            </p>
        ) : null}
    </div>
);
